import CardCommon from '@op/components/CardCommon';
import { cardIcon, tableIcon } from '@op/constants/chart';
import React, { useState } from 'react';
import FigureCard from '@op/pages/Overview/TrendingPublications/components/FigureCard';
import SvgIcon from '@/pages/home/components/SvgIcon';
import useLanguage from '@op/hooks/useLanguage';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import TableCommonHook from '@op/components/TableCommonHook';
import { useTrendingColumn } from '@op/components/TableCommonHook/hooks/useTrendingColumn';
import { SortBy } from 'tea-component/lib/table/addons';
import { useMultiGamesTrendingRequest } from '../hooks/useMultiGamesTrendingRequest';
import type { TGameMap } from '../hooks/useMultiGamesCond';
import '@op/pages/Overview/TrendingPublications/TrendingPosts/index.scss';
import '../index.scss';

import { LanguageType } from '@op/types/common';
import { goToFeedsReviewsDetail } from '@op/utils/url';
import { useHistory } from 'umi';

const sortByOptions = [
  { value: 'view', text: 'Views' },
  { value: 'engagement', text: 'Engagement' },
  { value: 'follower_number', text: 'Followers' },
  { value: 'tweets_reply', text: 'Comments' },
  { value: 'comment_time', text: 'Date' },
];

const sortColumns = sortByOptions.map((val) => val.value);

const extraFilter = { review_types: ['posts_text'] };

interface IZoneTrendingPostsProps {
  filter: any;
  gameMap: TGameMap;
  language: LanguageType;
  loadingHeight?: number;
  emptyHeight?: number;
}

export default function ZoneTrendingPosts({
  filter,
  gameMap,
  language,
  loadingHeight = 450,
  emptyHeight = 450,
}: IZoneTrendingPostsProps) {
  const { formatMessage } = useLanguage();
  const [view, setView] = useState('card');
  const [sortBy, setSortBy] = useState<SortBy<any>>({
    by: 'engagement',
    order: 'desc',
  });

  const { data, loading } = useMultiGamesTrendingRequest({
    filter,
    sortBy: { by: sortBy.by, order: sortBy.order || 'desc' },
    extraFilter,
  });

  const { columns } = useTrendingColumn({
    language,
    keys: [
      'game',
      'post',
      'language',
      'authorCountry',
      'commentTime',
      'view',
      'engagement',
      'subscribe',
      'tweetsReply',
    ],
    statistics: data?.statistics,
    logBtnId: 'TrendingPosts',
    gameMap,
  });

  const history = useHistory();

  return (
    <CardCommon
      title={formatMessage({ id: 'Trending Posts' })}
      operations={[cardIcon, tableIcon]}
      loading={loading}
      className="card--trend-posts"
      handleOperationsOnClick={(item) => {
        setView(item?.symbol || 'card');
      }}
      loadingHeight={loadingHeight}
      emptyHeight={emptyHeight}
      empty={data?.list?.length === 0}
    >
      <div className={`trend-video-list-wrapper`}>
        {view === 'card' ? (
          <>
            <SortBySelect
              defaultParams={{
                options: sortByOptions,
                btnTextPrefix: formatMessage({ id: 'Sort By' }),
              }}
              defaultValue={sortBy.by}
              onChange={(_key: any, val: string) => {
                setSortBy({ by: val, order: 'desc' });
              }}
            />
            <div className="trend-posts-list">
              {data?.list?.map((item: any, index: number) => {
                const game = gameMap.get(item.unified_id);
                return (
                  <div className="trend-posts-item" key={item.comment_uin || index}>
                    <FigureCard
                      size="large"
                      item={item}
                      language={language}
                      game={game}
                      navigatePage={(_e: any) => {
                        goToFeedsReviewsDetail(
                          history,
                          item,
                          game
                            ? { language, gameid: item.unified_id }
                            : { language },
                        );
                      }}
                      showGame
                    />
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="trend-video-table">
            <TableCommonHook
              recordKey={'comment_uin'}
              records={data?.list || []}
              total={data?.total || 0}
              columns={columns}
              sortColumns={sortColumns}
              hasPageable={false}
              loading={loading}
              sortInfo={[sortBy, setSortBy]}
              logBtnId={'TrendingPosts'}
            />
          </div>
        )}
      </div>
    </CardCommon>
  );
}
